import { useEffect, useRef } from 'react'

export interface StreamEvent<T = unknown> { type: string; id: string; ts: string; data: T }

// One shared EventSource to /api/v2/stream for the whole tab, opened by the first
// subscriber and closed when the last one unmounts (EventSource reconnects on its own).
type Handler = (e: StreamEvent) => void
let es: EventSource | null = null
const handlers = new Map<string, Set<Handler>>()
const attached = new Set<string>()

function ensure(type: string) {
  if (typeof EventSource === 'undefined') return
  if (closeTimer) { clearTimeout(closeTimer); closeTimer = null }
  if (!es) { es = new EventSource('/api/v2/stream', { withCredentials: true }); attached.clear() }
  if (attached.has(type)) return
  attached.add(type)
  es.addEventListener(type, (ev: Event) => {
    const msg = ev as MessageEvent<string>
    let body: { ts: string; data: unknown }
    try { body = JSON.parse(msg.data) as { ts: string; data: unknown } } catch { return }
    handlers.get(type)?.forEach(h => h({ type, id: msg.lastEventId, ts: body.ts, data: body.data }))
  })
}

// Close a few seconds after the last subscriber leaves, so page-to-page navigation keeps the connection.
let closeTimer: ReturnType<typeof setTimeout> | null = null
function release() {
  for (const set of handlers.values()) if (set.size) return
  if (closeTimer) clearTimeout(closeTimer)
  closeTimer = setTimeout(() => {
    closeTimer = null
    for (const set of handlers.values()) if (set.size) return
    es?.close(); es = null; attached.clear()
  }, 5000)
}

/**
 * Subscribe to /api/v2/stream (SSE). All hooks share one connection; the handler
 * ref keeps the latest closure without re-subscribing.
 */
export function useStream(types: string[], handler: (e: StreamEvent) => void, enabled = true) {
  const ref = useRef(handler)
  useEffect(() => { ref.current = handler }, [handler])
  const key = types.join(',')
  useEffect(() => {
    if (!enabled) return
    const fn: Handler = e => ref.current(e)
    const list = key.split(',')
    for (const type of list) {
      if (!handlers.has(type)) handlers.set(type, new Set())
      handlers.get(type)?.add(fn)
      ensure(type)
    }
    return () => { for (const type of list) handlers.get(type)?.delete(fn); release() }
  }, [key, enabled])
}

/**
 * Like useStream, but hands events over in batches (at most one call per `delayMs`, max 100 events).
 * A burst of new filings — e.g. after a server restart — then costs one request instead of one per
 * filing; per-filing fetches in a burst tripped the rate limiter and got a user's IP banned.
 */
export function useStreamBatch(types: string[], handler: (events: StreamEvent[]) => void, delayMs = 1500, enabled = true) {
  const ref = useRef(handler)
  useEffect(() => { ref.current = handler }, [handler])
  const queue = useRef<StreamEvent[]>([])
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])
  useStream(types, e => {
    queue.current.push(e)
    const flush = () => { timer.current = null; const batch = queue.current.splice(0, queue.current.length); if (batch.length) ref.current(batch) }
    if (queue.current.length >= 100) { if (timer.current) clearTimeout(timer.current); flush(); return }
    if (!timer.current) timer.current = setTimeout(flush, delayMs)
  }, enabled)
}
